// 프로토타입 "고급: 100% 요소 단위로 전체 연결" 결정론 매칭 휴리스틱.
//
// 클릭 요소의 라벨(텍스트/아이콘명)을 가장 잘 맞는 타깃 프레임에 매핑한다.
// 좌표는 렌더 시점에 PrototypeApp 이 findClickableByLabel 로 자동 계산하므로,
// 이 모듈은 오직 "라벨 → 어느 화면으로 갈지" 만 결정한다.
//
// DOM 비의존 순수 함수 — 노드에서 단위 테스트 가능
// (test/javascript/prototype_connect_matcher_test.mjs).

// 라벨이 특정 내비 의미군이면, hints 에 해당하는 이름의 프레임으로 연결한다.
export const NAV_SYNONYMS = [
  { keys: ["홈", "메인", "home", "main"], hints: ["홈", "메인", "home", "main", "대시보드", "dashboard"] },
  { keys: ["마이", "마이페이지", "내정보", "내 정보", "프로필", "내 계정", "mypage", "my", "profile", "account"], hints: ["마이", "프로필", "내정보", "계정", "mypage", "profile", "account"] },
  { keys: ["검색", "찾기", "search"], hints: ["검색", "search"] },
  { keys: ["알림", "notification", "noti", "알림함"], hints: ["알림", "notification"] },
  { keys: ["로그인", "login", "signin", "sign in"], hints: ["로그인", "login", "signin"] },
  { keys: ["회원가입", "가입하기", "가입", "signup", "sign up", "register"], hints: ["회원가입", "가입", "signup", "register"] },
  { keys: ["설정", "환경설정", "setting", "settings"], hints: ["설정", "환경설정", "setting"] },
  { keys: ["장바구니", "카트", "cart"], hints: ["장바구니", "카트", "cart"] },
  { keys: ["찜", "관심", "위시리스트", "wishlist", "favorite"], hints: ["찜", "관심", "위시", "wishlist", "favorite"] }
]

// 상세/더보기류 CTA → '상세'·'detail' 류 프레임으로
const CTA_DETAIL_HINTS = ["자세히", "상세", "더보기", "보기", "detail", "view", "more"]
const CTA_DETAIL_FRAME_RE = /(상세|detail|view|보기)/

// 플로우 진입 CTA → 해당 의미의 프레임으로
const CTA_FLOW = [
  { keys: ["신청", "지원", "apply"], hints: ["신청", "지원", "apply"] },
  { keys: ["예약", "booking", "reserve", "예약하기"], hints: ["예약", "booking", "reserve"] },
  { keys: ["결제", "구매", "주문", "payment", "checkout", "buy", "order"], hints: ["결제", "구매", "주문", "payment", "checkout", "order"] },
  { keys: ["시작하기", "시작", "get started", "start"], hints: ["시작", "start", "온보딩", "onboarding"] },
  { keys: ["작성", "글쓰기", "등록", "write", "create"], hints: ["작성", "글쓰기", "등록", "write", "create"] }
]

// 타깃 화면이 없는(또는 외부) 요소 — 연결 생성 자체를 건너뛴다
const SKIP_LABEL_PATTERNS = [
  /^https?:/i,
  /^\d+$/,                                                   // 페이지네이션 숫자
  /^(다음|이전|prev|next|»|«|<|>)$/i,
  /^(삭제|저장|취소|닫기|확인|적용|수정|편집|delete|save|cancel|close|ok|confirm|submit|apply|edit)$/i
]

const BACK_RE = /^(뒤로|뒤로가기|이전화면|back|goback)$/

const MATCH_THRESHOLD = 0.5

export function normalizeForMatch(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/icon:/g, " ")
    .replace(/[^0-9a-z가-힣]+/gi, " ")
    .trim()
}

function tokenize(s) {
  return normalizeForMatch(s).split(/\s+/).filter(Boolean)
}

function jaccard(a, b) {
  const A = new Set(tokenize(a))
  const B = new Set(tokenize(b))
  if (A.size === 0 || B.size === 0) return 0
  let inter = 0
  for (const t of A) if (B.has(t)) inter++
  return inter / (A.size + B.size - inter)
}

export function shouldSkipLabel(label) {
  const raw = String(label || "").trim()
  if (!raw) return true
  return SKIP_LABEL_PATTERNS.some((re) => re.test(raw))
}

// frames: [{ id, name }] — 캔버스 좌→우(흐름) 순으로 정렬된 상태를 권장.
// sourceIndex: 클릭 요소가 속한 소스 프레임의 frames 내 index.
// 반환: { targetFrameId, score, reason } 또는 null(연결 안 함).
export function matchElementToFrame(label, tag, sourceIndex, frames) {
  if (!Array.isArray(frames) || frames.length < 2) return null
  if (shouldSkipLabel(label)) return null

  const norm = normalizeForMatch(label)
  const compact = norm.replace(/\s+/g, "")
  if (!compact) return null

  const source = frames[sourceIndex]
  let best = null
  const consider = (frame, score, reason) => {
    if (!frame) return
    if (source && frame.id === source.id) return // 자기 자신 제외
    if (!best || score > best.score) best = { targetFrameId: frame.id, score, reason }
  }

  // 뒤로/back → 직전(좌측) 프레임
  if (BACK_RE.test(compact)) {
    const prev = frames[sourceIndex - 1]
    if (prev) consider(prev, 0.7, "back→prev")
  }

  for (let i = 0; i < frames.length; i++) {
    if (i === sourceIndex) continue
    const f = frames[i]
    const fname = normalizeForMatch(f.name)
    if (!fname) continue

    // ① 정확 일치
    if (norm === fname) { consider(f, 1.0, "exact"); continue }
    // ① 포함 일치(라벨이 프레임명을 포함하거나 그 반대) — 길이 비율로 가중
    if (norm.includes(fname) || fname.includes(norm)) {
      const ratio = Math.min(norm.length, fname.length) / Math.max(norm.length, fname.length)
      consider(f, 0.6 + 0.3 * ratio, "contains")
    }
    // ② 토큰 Jaccard(약한 신호)
    const j = jaccard(label, f.name)
    if (j > 0) consider(f, 0.4 + 0.4 * j, "jaccard")
  }

  // ③ 내비 동의어군 — 라벨이 동의어면 hints 와 이름이 맞는 프레임으로
  for (const grp of NAV_SYNONYMS) {
    const hit = grp.keys.some((k) => compact.includes(normalizeForMatch(k).replace(/\s+/g, "")))
    if (!hit) continue
    const target = frames.find((f, i) =>
      i !== sourceIndex && grp.hints.some((h) => normalizeForMatch(f.name).includes(normalizeForMatch(h)))
    )
    if (target) consider(target, 0.75, "nav-synonym")
  }

  // ④ 상세/더보기 CTA → 상세류 프레임
  if (CTA_DETAIL_HINTS.some((h) => norm.includes(normalizeForMatch(h)))) {
    const target = frames.find((f, i) => i !== sourceIndex && CTA_DETAIL_FRAME_RE.test(normalizeForMatch(f.name)))
    if (target) consider(target, 0.55, "cta-detail")
  }

  // ④ 플로우 진입 CTA → 해당 의미의 프레임
  for (const grp of CTA_FLOW) {
    if (!grp.keys.some((k) => norm.includes(normalizeForMatch(k)))) continue
    const target = frames.find((f, i) =>
      i !== sourceIndex && grp.hints.some((h) => normalizeForMatch(f.name).includes(normalizeForMatch(h)))
    )
    if (target) consider(target, 0.7, "cta-flow")
  }

  if (best && best.score >= MATCH_THRESHOLD) return best
  return null
}

// 한 프로젝트의 전 프레임에 대해 add_hotspot 액션 배열을 만든다.
// clickablesByIndex: sourceIndex → [{ label, tag }]  (DOM 추출은 호출측 책임)
// frames: [{ id, name }] (캔버스 좌→우 정렬 권장)
export function buildConnectionActions(frames, clickablesByIndex) {
  const actions = []
  const seen = new Set()
  for (let si = 0; si < frames.length; si++) {
    const src = frames[si]
    const clickables = clickablesByIndex[si] || []
    for (const el of clickables) {
      const m = matchElementToFrame(el.label, el.tag, si, frames)
      if (!m) continue
      const key = `${src.id}->${m.targetFrameId}:${el.label}`
      if (seen.has(key)) continue
      seen.add(key)
      actions.push({
        type: "add_hotspot",
        frame_id: src.id,
        target_frame_id: m.targetFrameId,
        label: el.label,
        target_label: el.label,
        target_tag: el.tag || null,
        x: 0, y: 0, width: 8, height: 8
      })
    }
  }
  return actions
}
